import { useState } from 'react'
import {
  Container,
  Typography,
  Box,
  Grid,
  Tabs,
  Tab,
  Stack,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  CircularProgress,
} from '@mui/material'
import { EmojiEvents, WorkspacePremium, Leaderboard, TrendingUp } from '@mui/icons-material'
import { useAuthStore } from '@/store/useAuthStore'
import { useFamilyStore } from '@/store/useFamilyStore'
import { useAchievementStore } from '@/store/useAchievementStore'
import { useAchievements } from '@/hooks/useAchievements'
import BadgeCard from '@/components/gamification/BadgeCard'
import AchievementUnlock from '@/components/gamification/AchievementUnlock'
import LevelProgress from '@/components/gamification/LevelProgress'
import LeaderboardCard from '@/components/gamification/LeaderboardCard'
import type { AchievementCategory } from '@/types/achievements'

export default function GamificationPage() {
  const [selectedTab, setSelectedTab] = useState(0)
  const [categoryFilter, setCategoryFilter] = useState<AchievementCategory | 'All'>('All')
  const [showUnlockedOnly, setShowUnlockedOnly] = useState(false)

  const currentUserId = useAuthStore((state) => state.currentUserId)
  const members = useFamilyStore((state) => state.members)
  const { achievementProgress, loading, recentUnlocks, clearRecentUnlocks } = useAchievementStore()

  // Initialize achievements
  useAchievements()

  const currentUser = members.find((m) => m.id === currentUserId)

  // Filter achievements based on category and unlocked status
  const filteredAchievements = achievementProgress.filter((achievement) => {
    const categoryMatch = categoryFilter === 'All' || achievement.category === categoryFilter
    const unlockedMatch = !showUnlockedOnly || achievement.unlocked
    return categoryMatch && unlockedMatch
  })

  // Group achievements by category for stats
  const achievementStats = {
    total: achievementProgress.length,
    unlocked: achievementProgress.filter((a) => a.unlocked).length,
    byCategory: achievementProgress.reduce((acc, achievement) => {
      const category = achievement.category
      if (!acc[category]) {
        acc[category] = { total: 0, unlocked: 0 }
      }
      acc[category].total++
      if (achievement.unlocked) {
        acc[category].unlocked++
      }
      return acc
    }, {} as Record<AchievementCategory, { total: number; unlocked: number }>),
  }

  const categories: (AchievementCategory | 'All')[] = ['All', 'Tasks', 'Streak', 'Speed', 'Quality', 'Inventory', 'Special']

  return (
    <Container maxWidth="lg">
      {/* Header */}
      <Box mb={4}>
        <Typography
          variant="h4"
          gutterBottom
          sx={{
            fontWeight: 700,
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 1,
          }}
        >
          Achievements & Rewards
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.125rem' }}>
          Track your progress and unlock achievements
        </Typography>
      </Box>

      {/* Level Progress */}
      {currentUser && (
        <Box mb={4}>
          <LevelProgress points={currentUser.points} userName={currentUser.name} />
        </Box>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              color: 'white',
            }}
          >
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <WorkspacePremium sx={{ fontSize: 24 }} />
                <Typography variant="body2" sx={{ fontWeight: 600, opacity: 0.95 }}>
                  Total Achievements
                </Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>
                {achievementStats.unlocked}/{achievementStats.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: 'white',
            }}
          >
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <TrendingUp sx={{ fontSize: 24 }} />
                <Typography variant="body2" sx={{ fontWeight: 600, opacity: 0.95 }}>
                  Completion Rate
                </Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>
                {achievementStats.total > 0
                  ? Math.round((achievementStats.unlocked / achievementStats.total) * 100)
                  : 0}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              color: 'white',
            }}
          >
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <EmojiEvents sx={{ fontSize: 24 }} />
                <Typography variant="body2" sx={{ fontWeight: 600, opacity: 0.95 }}>
                  Total Points
                </Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>
                {currentUser?.points || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)',
              color: 'white',
            }}
          >
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Leaderboard sx={{ fontSize: 24 }} />
                <Typography variant="body2" sx={{ fontWeight: 600, opacity: 0.95 }}>
                  Your Rank
                </Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>
                #{members.findIndex((m) => m.id === currentUserId) + 1}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Box mb={4}>
        <Tabs
          value={selectedTab}
          onChange={(_, newValue) => setSelectedTab(newValue)}
          sx={{
            '& .MuiTab-root': {
              fontWeight: 600,
              fontSize: '1rem',
            },
          }}
        >
          <Tab label="Achievements" icon={<WorkspacePremium />} iconPosition="start" />
          <Tab label="Leaderboard" icon={<Leaderboard />} iconPosition="start" />
        </Tabs>
      </Box>

      {/* Achievements Tab */}
      {selectedTab === 0 && (
        <>
          {/* Filters */}
          <Box mb={3}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
              <ButtonGroup size="small" sx={{ flexWrap: 'wrap' }}>
                {categories.map((category) => (
                  <Button
                    key={category}
                    onClick={() => setCategoryFilter(category)}
                    variant={categoryFilter === category ? 'contained' : 'outlined'}
                  >
                    {category}
                  </Button>
                ))}
              </ButtonGroup>

              <Button
                size="small"
                onClick={() => setShowUnlockedOnly(!showUnlockedOnly)}
                variant={showUnlockedOnly ? 'contained' : 'outlined'}
              >
                {showUnlockedOnly ? 'Show All' : 'Unlocked Only'}
              </Button>
            </Stack>
          </Box>

          {/* Achievement Grid */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : filteredAchievements.length === 0 ? (
            <Card>
              <CardContent>
                <Typography variant="body1" color="text.secondary" textAlign="center" py={4}>
                  No achievements match your filters
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <Grid container spacing={3}>
              {filteredAchievements.map((achievement) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={achievement.achievement_id}>
                  <BadgeCard achievement={achievement} />
                </Grid>
              ))}
            </Grid>
          )}
        </>
      )}

      {/* Leaderboard Tab */}
      {selectedTab === 1 && (
        <LeaderboardCard members={members} currentUserId={currentUserId} />
      )}

      {/* Achievement Unlock Dialog */}
      <AchievementUnlock
        achievement={recentUnlocks[0] || null}
        open={recentUnlocks.length > 0}
        onClose={() => clearRecentUnlocks()}
      />
    </Container>
  )
}
